import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  CvWriteCoordinator,
  cvRevisionTarget,
  type CvWriteOptions,
} from '../lib/cv-write-coordinator';
import type { CvWritePolicy } from '../lib/cv-write-policy';
import { queryCacheCvRevisions } from '../lib/cv-revision-cache';
import { invalidateCvDocumentQueries } from '../lib/cv-query-invalidation';
import { promptCvRevisionConflict } from '../lib/cv-conflict-prompt';

/**
 * React glue for the CV write coordinator (B02, lib/cv-write-coordinator.ts).
 * One coordinator per QueryClient: its latest-revision memory lives in that
 * client's cache, a conflict refetches that client's CV queries, and the
 * prompt is the platform dialog (native Alert / web in-app dialog).
 */
const coordinators = new WeakMap<QueryClient, CvWriteCoordinator>();

export function getCvWriteCoordinator(queryClient: QueryClient): CvWriteCoordinator {
  let coordinator = coordinators.get(queryClient);
  if (!coordinator) {
    coordinator = new CvWriteCoordinator({
      revisions: queryCacheCvRevisions(queryClient),
      refresh: (target) => invalidateCvDocumentQueries(queryClient, target),
      prompt: (kind) => promptCvRevisionConflict(kind),
    });
    coordinators.set(queryClient, coordinator);
  }
  return coordinator;
}

/**
 * Makes sure revisions read by this screen are recorded (CV reads report the
 * revision they returned; the coordinator listens). Call from every hook that
 * reads a CV document the editor saves back.
 */
export function useCvRevisionTracking(): CvWriteCoordinator {
  return getCvWriteCoordinator(useQueryClient());
}

/** A CV write call; `write` is undefined when the request must stay as before. */
export type CvWriteSend<T> = (write: CvWriteOptions | undefined) => Promise<T>;

/**
 * Runs the CV writes of one editor surface. The surface's draft base (its
 * lease) is the revision known when the hook mounted: a sheet or edit page
 * was filled from that version. Writes to the main profile (no CV target)
 * and writes whose policy states no base are sent exactly as before.
 */
export function useCvWriteRunner(target: string | null | undefined) {
  const coordinator = useCvRevisionTracking();
  const cvTarget = cvRevisionTarget(target);
  const lease = useMemo(
    () => (cvTarget ? coordinator.createLease(cvTarget) : null),
    [coordinator, cvTarget],
  );
  useEffect(() => {
    if (!lease) return undefined;
    coordinator.attachLease(lease);
    return () => coordinator.detachLease(lease);
  }, [coordinator, lease]);

  return useCallback(
    <T>(policy: CvWritePolicy, send: CvWriteSend<T>): Promise<T> => {
      if (!cvTarget || policy.base === 'none') return send(undefined);
      return coordinator.run<T>(cvTarget, {
        kind: policy.kind,
        coalesceKey: policy.coalesceKey,
        lease: policy.base === 'draft' ? lease : null,
        send: async (ifMatch) => {
          let revision: number | undefined;
          const value = await send({
            ifMatch,
            onRevision: (stored) => {
              revision = stored;
            },
          });
          return { value, revision };
        },
      });
    },
    [coordinator, cvTarget, lease],
  );
}
